import * as React from 'react';
import moment from 'moment';
import { Report } from '../../models/report';
import './AvailabilityStatusDot.scss';

export type AvailabilityDotStatus = 'available' | 'unavailable' | 'pending' | 'expired' | 'not-requested' | 'no-eligible' | 'no-report';

export function getAvailabilityDotStatus(report?: Report, selfService = false): AvailabilityDotStatus {
  if (!report) return selfService ? 'no-report' : 'not-requested';
  if ((report as any).availabilityNoEligibleRecipient) return 'no-eligible';
  if (typeof report.isTakePart === 'boolean') return report.isTakePart ? 'available' : 'unavailable';
  const deadline = (report as any).deadlineForAnswers;
  return deadline && moment().isSameOrAfter(moment(deadline)) ? 'expired' : 'pending';
}

const LABELS: Record<AvailabilityDotStatus, string> = {
  available: 'Available', unavailable: 'Not available', pending: 'Pending', expired: 'Expired',
  'not-requested': 'Not requested', 'no-eligible': 'No eligible recipient', 'no-report': 'No report yet'
};

function tooltipFor(status: AvailabilityDotStatus, report?: Report): string {
  if (status === 'available' || status === 'unavailable') {
    const sender = report?.sender?.fullName || 'Family';
    const studentId = String(report?.playerDetails?.userId || '');
    const role = String(report?.sender?.userId || '') === studentId ? 'Student' : 'Parent';
    const date = report?.createdAt && moment(report.createdAt).isValid() ? moment(report.createdAt).format('D MMM, HH:mm') : '';
    return [LABELS[status], `${sender} (${role})${date ? ` · ${date}` : ''}`, report?.details ? `“${report.details}”` : ''].filter(Boolean).join('\n');
  }
  if (status === 'pending') {
    const sent = report?.createdAt && moment(report.createdAt).isValid() ? `Requested ${moment(report.createdAt).format('D MMM')}` : 'Availability requested';
    const deadline = (report as any)?.deadlineForAnswers;
    return `${sent}${deadline ? ` · reply by ${moment(deadline).format('D MMM, HH:mm')}` : ''}`;
  }
  if (status === 'expired') return `No reply by ${moment((report as any).deadlineForAnswers).format('D MMM, HH:mm')}`;
  if (status === 'no-eligible') return 'No linked parent, no student account';
  if (status === 'no-report') return 'Family hasn’t reported';
  return 'Not included in a request yet';
}

export function AvailabilityStatusDot({ report, selfService = false }: { report?: Report; selfService?: boolean }) {
  const status = getAvailabilityDotStatus(report, selfService);
  const isNew = !!report && typeof report.isTakePart === 'boolean' && !report.isActionPerformed;
  const tooltip = tooltipFor(status, report);
  return <span className="availabilityDotWrap">
    <span className={`availabilityStatusDot is-${status}`} title={tooltip} aria-label={tooltip} tabIndex={0} />
    {isNew ? <span className="availabilityStatusDot__new">NEW</span> : null}
  </span>;
}

export function AvailabilityStatusLegend({ reports, permissionIds, selfService = false }: { reports: Report[]; permissionIds: string[]; selfService?: boolean }) {
  const latest = reports.reduce((map, report) => {
    const key = String(report.playerDetails?.permissionId || '');
    const previous = map.get(key);
    if (key && (!previous || moment(report.createdAt).isAfter(moment(previous.createdAt)))) map.set(key, report);
    return map;
  }, new Map<string, Report>());
  const counts = permissionIds.reduce((all, permissionId) => {
    const status = getAvailabilityDotStatus(latest.get(String(permissionId)), selfService);
    all[status] = (all[status] || 0) + 1;
    return all;
  }, {} as Record<AvailabilityDotStatus, number>);
  const order: AvailabilityDotStatus[] = ['available','unavailable','pending','expired','not-requested','no-report','no-eligible'];
  const available = counts.available || 0;
  const reachable = permissionIds.length - (counts['no-eligible'] || 0);
  return <div className="availabilityStatusSummary">
    <div className="availabilityStatusSummary__score"><strong>{available} of {reachable}</strong> available</div>
    <div className="availabilityStatusSummary__legend" aria-label="Availability statuses">
      {order.filter(status => counts[status]).map(status => <span key={status}><span className={`availabilityStatusDot is-${status}`} />{LABELS[status]} <b>{counts[status]}</b></span>)}
    </div>
  </div>;
}
